import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Search,
  Calendar,
  FileDown,
  ChevronDown,
  ChevronRight,
  Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchPurchases, deletePurchaseById } from '../../redux/slices/purchaseSlice';
import { format } from 'date-fns';

const PurchaseList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { purchases, loading } = useSelector((state) => state.purchase);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('All');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  // Expanded purchases state
  const [expandedPurchases, setExpandedPurchases] = useState({});

  // Load purchases on mount
  useEffect(() => {
    dispatch(fetchPurchases());
  }, [dispatch]);

  // Toggle purchase expansion
  const togglePurchase = (purchaseId) => {
    setExpandedPurchases(prev => ({
      ...prev,
      [purchaseId]: !prev[purchaseId]
    }));
  };

  // Handle delete purchase
  const handleDeletePurchase = async (purchaseId) => {
    if (window.confirm('Are you sure you want to delete this purchase?')) {
      try {
        await dispatch(deletePurchaseById(purchaseId)).unwrap();
        toast.success('Purchase deleted successfully');
      } catch (error) {
        toast.error('Failed to delete purchase: ' + error);
      }
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setPaymentTypeFilter('All');
    setFromDate(new Date().toISOString().split('T')[0]);
    setToDate(new Date().toISOString().split('T')[0]);
  };

  // Handle search
  const handleSearch = () => {
    toast.success('Filters applied');
  };

  // Handle export
  const handleExport = () => {
    toast.info('Export functionality coming soon');
  };

  // Handle schedule report
  const handleScheduleReport = () => {
    toast.info('Schedule Report feature coming soon');
  };

  // Filter purchases
  const filteredPurchases = purchases.filter((purchase) => {
    const matchesSearch =
      purchase.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.supplierName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPaymentType =
      paymentTypeFilter === 'All' || purchase.purchaseType === paymentTypeFilter.toLowerCase();

    return matchesSearch && matchesPaymentType;
  });

  // Calculate grand totals
  const grandTotals = filteredPurchases.reduce(
    (acc, purchase) => {
      const purchaseItemTotals = purchase.items?.reduce(
        (itemAcc, item) => ({
          quantity: itemAcc.quantity + (parseFloat(item.quantity) || 0),
          discountAmount: itemAcc.discountAmount + (parseFloat(item.discountAmount) || 0),
          taxAmount: itemAcc.taxAmount + ((parseFloat(item.landingCost) * parseFloat(item.gst)) / (100 + parseFloat(item.gst))),
          totalAmount: itemAcc.totalAmount + (parseFloat(item.landingCost) || 0),
        }),
        { quantity: 0, discountAmount: 0, taxAmount: 0, totalAmount: 0 }
      ) || { quantity: 0, discountAmount: 0, taxAmount: 0, totalAmount: 0 };

      return {
        quantity: acc.quantity + purchaseItemTotals.quantity,
        discountAmount: acc.discountAmount + purchaseItemTotals.discountAmount,
        taxAmount: acc.taxAmount + purchaseItemTotals.taxAmount,
        totalAmount: acc.totalAmount + purchaseItemTotals.totalAmount,
      };
    },
    { quantity: 0, discountAmount: 0, taxAmount: 0, totalAmount: 0 }
  );

  return (
    <div className='max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen'>
      {/* Header */}
      <div className='mb-4 md:mb-6 flex justify-between items-start'>
        <div>
          <h1 className='text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 md:mb-2'>
            Purchase List
          </h1>
          <p className='text-[11px] md:text-sm text-gray-600'>
            Track and manage all purchase transactions • {filteredPurchases.length} purchases
          </p>
        </div>
        <div className='flex gap-2'>
          <button
            onClick={handleScheduleReport}
            className='hidden md:flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all text-[11px] md:text-sm font-medium text-gray-700 shadow-sm'
          >
            <Clock className='w-4 h-4' />
            <span>Schedule Report</span>
          </button>
          <div className='relative'>
            <button
              onClick={handleExport}
              className='flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all text-[11px] md:text-sm font-medium text-gray-700 shadow-sm'
            >
              <FileDown className='w-4 h-4' />
              <span className='hidden md:inline'>Export</span>
              <ChevronDown className='w-3 h-3' />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4'>
          {/* Search */}
          <div className='lg:col-span-2'>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              Search Purchase
            </label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <input
                type='text'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder='Search by invoice, supplier...'
                className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
              />
            </div>
          </div>

          {/* Payment Type Filter */}
          <div>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              Payment Type
            </label>
            <select
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
            >
              <option value='All'>All Types</option>
              <option value='Cash'>Cash</option>
              <option value='Online'>Online</option>
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              From Date
            </label>
            <div className='relative'>
              <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <input
                type='date'
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
              />
            </div>
          </div>

          {/* To Date */}
          <div>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              To Date
            </label>
            <div className='relative'>
              <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
              <input
                type='date'
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex gap-2 mt-4'>
          <button
            onClick={handleSearch}
            className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium text-[13px]'
          >
            Search
          </button>
          <button
            onClick={handleClearFilters}
            className='px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium text-[13px]'
          >
            Clear
          </button>
          <button
            onClick={() => navigate('/admin/purchase-details')}
            className='ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium text-[13px]'
          >
            <Plus className='w-4 h-4' />
            <span>Add Purchase</span>
          </button>
        </div>
      </div>

      {/* Grouped Purchase Table */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='bg-gray-50 border-b-2 border-gray-300'>
              <tr>
                <th className='px-3 py-3 text-left text-[11px] font-bold text-gray-700 uppercase w-8'></th>
                <th className='px-3 py-3 text-left text-[11px] font-bold text-gray-700 uppercase'>
                  Code
                </th>
                <th className='px-3 py-3 text-left text-[11px] font-bold text-gray-700 uppercase'>
                  Description
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  MRP
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  UOM
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  Purchase Rate
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  Qty
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  Discount Amt
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  Tax Amt (%)
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  Amount
                </th>
                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan='11' className='px-6 py-12 text-center'>
                    <div className='flex justify-center items-center'>
                      <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
                    </div>
                  </td>
                </tr>
              ) : filteredPurchases.length === 0 ? (
                <tr>
                  <td colSpan='11' className='px-6 py-12 text-center text-gray-500 text-sm'>
                    No purchases found
                  </td>
                </tr>
              ) : (
                <>
                  {filteredPurchases.map((purchase) => {
                    const isExpanded = expandedPurchases[purchase.id];
                    const purchaseTotals = purchase.items?.reduce(
                      (acc, item) => ({
                        quantity: acc.quantity + (parseFloat(item.quantity) || 0),
                        discountAmount: acc.discountAmount + (parseFloat(item.discountAmount) || 0),
                        taxAmount: acc.taxAmount + ((parseFloat(item.landingCost) * parseFloat(item.gst)) / (100 + parseFloat(item.gst))),
                        totalAmount: acc.totalAmount + (parseFloat(item.landingCost) || 0),
                      }),
                      { quantity: 0, discountAmount: 0, taxAmount: 0, totalAmount: 0 }
                    ) || { quantity: 0, discountAmount: 0, taxAmount: 0, totalAmount: 0 };

                    return (
                      <React.Fragment key={purchase.id}>
                        {/* Purchase Header Row */}
                        <tr className='bg-blue-50 border-t-2 border-blue-300'>
                          <td className='px-3 py-3'>
                            <button
                              onClick={() => togglePurchase(purchase.id)}
                              className='p-1 hover:bg-blue-100 rounded transition-colors'
                            >
                              {isExpanded ? (
                                <ChevronDown className='w-4 h-4 text-gray-700' />
                              ) : (
                                <ChevronRight className='w-4 h-4 text-gray-700' />
                              )}
                            </button>
                          </td>
                          <td colSpan='2' className='px-3 py-3'>
                            <div className='flex items-center gap-4'>
                              <div>
                                <span className='text-[11px] text-gray-600'>Supplier Name:</span>
                                <span className='ml-2 text-[13px] font-bold text-gray-900'>
                                  {purchase.supplierName}
                                </span>
                              </div>
                              <div>
                                <span className='text-[11px] text-gray-600'>Pur.No:</span>
                                <span className='ml-2 text-[13px] font-semibold text-gray-900'>
                                  {purchase.id?.slice(0, 8)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td colSpan='3' className='px-3 py-3'>
                            <div className='flex items-center gap-4'>
                              <div>
                                <span className='text-[11px] text-gray-600'>Pur.Date:</span>
                                <span className='ml-2 text-[13px] font-semibold text-gray-900'>
                                  {purchase.purchaseDate
                                    ? format(new Date(purchase.purchaseDate), 'dd/MM/yyyy')
                                    : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className='text-[11px] text-gray-600'>Pur.Type:</span>
                                <span className='ml-2 text-[13px] font-semibold text-gray-900 uppercase'>
                                  {purchase.purchaseType}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td colSpan='3' className='px-3 py-3'>
                            <div className='flex items-center gap-4'>
                              <div>
                                <span className='text-[11px] text-gray-600'>Supp.Inv.No:</span>
                                <span className='ml-2 text-[13px] font-semibold text-gray-900'>
                                  {purchase.invoiceNumber}
                                </span>
                              </div>
                              <div>
                                <span className='text-[11px] text-gray-600'>Supp.Inv.Date:</span>
                                <span className='ml-2 text-[13px] font-semibold text-gray-900'>
                                  {purchase.invoiceDate
                                    ? format(new Date(purchase.invoiceDate), 'dd/MM/yyyy')
                                    : (purchase.purchaseDate ? format(new Date(purchase.purchaseDate), 'dd/MM/yyyy') : 'N/A')}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className='px-3 py-3 text-center'>
                            <button
                              onClick={() => handleDeletePurchase(purchase.id)}
                              className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                              title='Delete'
                            >
                              <Trash2 className='w-4 h-4' />
                            </button>
                          </td>
                        </tr>

                        {/* Purchase Items (Expandable) */}
                        {isExpanded && purchase.items?.map((item, index) => {
                          const itemAmount = parseFloat(item.landingCost) || 0;
                          const itemGst = parseFloat(item.gst) || 0;
                          // Tax amount calculation: (Total Amount * GST) / (100 + GST) if amount is inclusive
                          const taxAmount = (itemAmount * itemGst) / (100 + itemGst);

                          return (
                            <tr key={index} className='hover:bg-gray-50 border-b border-gray-100'>
                              <td className='px-3 py-2'></td>
                              <td className='px-3 py-2 text-[13px] text-gray-900'>{item.productCode}</td>
                              <td className='px-3 py-2 text-[13px] text-gray-900 font-medium'>
                                {item.productName}
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                {parseFloat(item.mrp || 0).toFixed(2)}
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] text-gray-900 uppercase'>
                                {item.uom}
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                {parseFloat(item.purchaseRate || 0).toFixed(2)}
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] font-semibold text-gray-900'>
                                {item.quantity}
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                {parseFloat(item.discountAmount || 0).toFixed(2)}
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                {isNaN(taxAmount) ? '0.00' : taxAmount.toFixed(2)} ({itemGst}%)
                              </td>
                              <td className='px-3 py-2 text-center text-[13px] font-bold text-gray-900'>
                                {itemAmount.toFixed(2)}
                              </td>
                              <td className='px-3 py-2'></td>
                            </tr>
                          );
                        })}

                        {/* Purchase Total Row */}
                        {isExpanded && (
                          <tr className='bg-gray-100 border-b-2 border-gray-300'>
                            <td colSpan='6' className='px-3 py-2 text-right text-[13px] font-bold text-gray-900'>
                              Invoice Total
                            </td>
                            <td className='px-3 py-2 text-center text-[13px] font-bold text-blue-700'>
                              {purchaseTotals.quantity.toFixed(2)}
                            </td>
                            <td className='px-3 py-2 text-center text-[13px] font-bold text-gray-900'>
                              {purchaseTotals.discountAmount.toFixed(2)}
                            </td>
                            <td className='px-3 py-2 text-center text-[13px] font-bold text-gray-900'>
                              {purchaseTotals.taxAmount.toFixed(2)}
                            </td>
                            <td className='px-3 py-2 text-center text-[13px] font-bold text-green-700'>
                              {purchaseTotals.totalAmount.toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Grand Total */}
                  <tr className='bg-blue-100 border-t-2 border-blue-500'>
                    <td colSpan='6' className='px-3 py-3 text-right text-[15px] font-bold text-blue-900'>
                      Over All Total
                    </td>
                    <td className='px-3 py-3 text-center text-[15px] font-bold text-blue-900'>
                      {grandTotals.quantity.toFixed(2)}
                    </td>
                    <td className='px-3 py-3 text-center text-[15px] font-bold text-blue-900'>
                      {grandTotals.discountAmount.toFixed(2)}
                    </td>
                    <td className='px-3 py-3 text-center text-[15px] font-bold text-blue-900'>
                      {grandTotals.taxAmount.toFixed(2)}
                    </td>
                    <td className='px-3 py-3 text-center text-[15px] font-bold text-blue-900'>
                      ₹{grandTotals.totalAmount.toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseList;
